import React from "react";
import { useParams } from "react-router-dom";
import { trpc } from "~/lib/trpc";
import { DealChip } from "~/components/ui/DealChip";
import { formatEur, formatKm, formatRelative, type DealRating } from "~/lib/utils";
import { useT } from "~/lib/i18n";
import { computeSignals, partitionSignals, daysOnMarket, type Signal } from "~/lib/signals";

const DRIVE_TYPE_LABELS: Record<string, string> = {
  fwd: "Traction avant (FWD)",
  rwd: "Propulsion (RWD)",
  awd: "Intégrale (AWD)",
};

export default function ListingDetail(): React.JSX.Element {
  const { t, numberLocale } = useT();
  const { id = "" } = useParams();
  const utils = trpc.useUtils();
  const listingQ = trpc.listings.byId.useQuery({ id }, { enabled: !!id });
  const dealQ = trpc.fairPrice.forListing.useQuery({ listingId: id }, { enabled: !!id });
  const historyQ = trpc.listings.history.useQuery({ listingId: id, sinceDays: 90 }, { enabled: !!id });
  const imagesQ = trpc.images.signedUrlsForListing.useQuery(
    { listingId: id, limit: 30 },
    { enabled: !!id },
  );
  const optionsQ = trpc.listings.options.useQuery({ listingId: id }, { enabled: !!id });
  const enrichMut = trpc.listings.enrichOptions.useMutation({
    onSuccess: () => {
      void utils.listings.options.invalidate({ listingId: id });
      void utils.listings.byId.invalidate({ id });
    },
  });

  const l = listingQ.data;
  const deal = dealQ.data;
  const images = imagesQ.data ?? [];
  const rating = (deal?.dealRating ?? "unknown") as DealRating;

  const signals = React.useMemo<Signal[]>(() => {
    if (!l) return [];
    return computeSignals(
      {
        dealRating: rating,
        dealSavingsEur: deal?.dealSavingsEur ?? null,
        dealSavingsRawEur: (deal as { dealSavingsRawEur?: number | null } | null | undefined)?.dealSavingsRawEur ?? null,
        fairPriceEur: deal?.fairPriceEur ?? null,
        fairPriceConfidence: deal?.fairPriceConfidence ?? null,
        savingsSuspect: (deal as { savingsSuspect?: boolean } | null | undefined)?.savingsSuspect ?? false,
        currentPriceEur: l.currentPriceEur != null ? Number(l.currentPriceEur) : null,
        currentKm: l.currentKm ?? null,
        currentYear: l.currentYear ?? null,
        firstSeenAt: l.firstSeenAt ?? null,
        photoCount: (l as { snapPhotoCount?: number | null }).snapPhotoCount ?? null,
        sellerType: (l as { snapSellerType?: string | null }).snapSellerType ?? null,
      },
      { t: t as (key: string, vars?: Record<string, string | number>) => string, numberLocale },
    );
  }, [l, deal, rating, t, numberLocale]);
  const { pros, cons, risks } = partitionSignals(signals);

  React.useEffect(() => {
    if (l?.currentTitle) {
      document.title = `${l.currentTitle} — MotorScan`;
    }
    return () => { document.title = "MotorScan — The EU car scanner"; };
  }, [l?.currentTitle]);

  if (listingQ.isPending) {
    return (
      <div className="w-full max-w-[1280px] mx-auto px-5 py-10">
        <div className="space-y-4">
          <div className="h-8 w-2/3 skeleton-shimmer rounded" />
          <div className="h-[50vh] skeleton-shimmer rounded-lg" />
          <div className="grid grid-cols-2 gap-4">
            <div className="h-24 skeleton-shimmer rounded" />
            <div className="h-24 skeleton-shimmer rounded" />
          </div>
        </div>
      </div>
    );
  }
  if (!l) {
    return <div className="w-full max-w-[1280px] mx-auto px-5 py-10 text-ink-tertiary">{t("web.listing.notFound")}</div>;
  }

  return (
    <>
      <article className="w-full max-w-[1440px] mx-auto">
        {/* Hero Section: title + gallery */}
        <header className="px-5 pt-6 pb-4">
          <div className="flex items-center gap-2.5 mb-2">
            <SourceBadge source={l.source} />
            {l.currentCountry && (
              <span className="mono text-[10.5px] uppercase tracking-[0.1em] text-ink-tertiary bg-surface-sunken px-2 py-0.5 rounded-[3px]">
                {l.currentCountry}
              </span>
            )}
          </div>
          <h1 className="font-display text-[28px] sm:text-[36px] lg:text-[42px] leading-[1.15] text-ink-primary">
            {l.currentTitle ?? t("web.listing.untitled")}
          </h1>
          <div className="mt-2 flex items-center gap-3 text-[12.5px] text-ink-tertiary flex-wrap">
            {l.currentYear && <span className="mono font-medium text-ink-secondary">{l.currentYear}</span>}
            {l.currentKm != null && <span className="mono">{formatKm(l.currentKm, numberLocale)}</span>}
            <span>{t("web.listing.seenPrefix", { when: formatRelative(l.lastSeenAt, numberLocale) })}</span>
          </div>
        </header>

        {/* Full-width gallery */}
        <Gallery images={images} pendingLabel={t("web.listing.photosPending")} />

        {/* Decision Block — "Decide in 60 seconds" */}
        {(pros.length > 0 || cons.length > 0 || risks.length > 0) && (
          <DecisionBlock
            pros={pros}
            cons={cons}
            risks={risks}
            deal={deal}
            price={l.currentPriceEur != null ? Number(l.currentPriceEur) : null}
            days={daysOnMarket(l)}
          />
        )}

        {/* Content grid: main + sidebar */}
        <div className="px-5 py-8 grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-10">
          {/* Left column */}
          <div className="space-y-8">
            {/* Deal Analysis Card */}
            {deal && deal.fairPriceEur != null && (
              <DealAnalysis deal={deal} price={l.currentPriceEur != null ? Number(l.currentPriceEur) : null} />
            )}

            {/* Specs */}
            <SpecsGrid listing={l} />

            {/* Description (now populated from latest snapshot) */}
            {l.description && (
              <section>
                <SectionLabel>{t("web.listing.description")}</SectionLabel>
                <p className="mt-3 text-[14px] leading-relaxed text-ink-secondary whitespace-pre-line">
                  {l.description}
                </p>
              </section>
            )}

            {/* Options & Equipment */}
            <OptionsSection
              options={optionsQ.data ?? []}
              isLoading={optionsQ.isPending}
              listingId={id}
              llmEnrichedAt={(l as { llmEnrichedAt?: string | null }).llmEnrichedAt ?? null}
              onEnrich={() => enrichMut.mutate({ listingId: id })}
              enriching={enrichMut.isPending}
            />
          </div>

          {/* Right sidebar */}
          <aside className="space-y-6">
            {/* Price block */}
            <div className="sticky top-20 space-y-6">
              <div className="bg-surface-card border border-[var(--divider-weak)] rounded-lg p-5">
                <div className="flex items-baseline gap-3">
                  <span className="font-display text-[32px] sm:text-[38px] leading-[1] text-ink-primary tabular-nums">
                    {formatEur(l.currentPriceEur != null ? Number(l.currentPriceEur) : null, { locale: numberLocale })}
                  </span>
                  <DealChip rating={rating} />
                </div>

                {deal && deal.dealSavingsEur != null && deal.dealSavingsEur > 0 ? (
                  <div className="mt-2 text-[14px] font-medium text-deal-super">
                    {t("web.signal.belowMarket", {
                      amount: formatEur(deal.dealSavingsEur, { locale: numberLocale }),
                    })}
                  </div>
                ) : (deal as { savingsSuspect?: boolean } | null | undefined)?.savingsSuspect ? (
                  <div className="mt-2 text-[12.5px] text-amber-700 dark:text-amber-400">
                    {t("web.signal.savingsSuspectDetail")}
                  </div>
                ) : null}

                {/* Percentile gauge */}
                {deal && deal.fairPriceP10Eur != null && deal.fairPriceP90Eur != null && l.currentPriceEur != null && (
                  <PercentileGauge
                    price={Number(l.currentPriceEur)}
                    p10={deal.fairPriceP10Eur}
                    p90={deal.fairPriceP90Eur}
                    median={deal.fairPriceEur!}
                  />
                )}

                <a
                  href={l.canonicalUrl}
                  target="_blank"
                  rel="noopener"
                  className="mt-5 inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-[var(--accent)] text-[14px] font-medium text-[var(--ink-on-accent)] hover:bg-[var(--accent-hover)] transition-colors duration-200 shadow-sm"
                >
                  <ExternalLinkIcon />
                  {t("web.listing.openSource")}
                </a>
              </div>

              {/* Fair price breakdown */}
              {deal && deal.fairPriceEur != null && (
                <div className="bg-surface-card border border-[var(--divider-weak)] rounded-lg p-5">
                  <SectionLabel>{t("web.listing.marketData")}</SectionLabel>
                  <div className="mt-3 grid grid-cols-2 gap-y-2.5 text-[13px]">
                    <span className="text-ink-tertiary">{t("web.listing.fairEstimated")}</span>
                    <span className="text-right mono font-medium">{formatEur(deal.fairPriceEur, { locale: numberLocale })}</span>
                    <span className="text-ink-tertiary">{t("web.listing.p10")}</span>
                    <span className="text-right mono">{formatEur(deal.fairPriceP10Eur, { locale: numberLocale })}</span>
                    <span className="text-ink-tertiary">{t("web.listing.p90")}</span>
                    <span className="text-right mono">{formatEur(deal.fairPriceP90Eur, { locale: numberLocale })}</span>
                    <span className="text-ink-tertiary">{t("web.listing.dealScore")}</span>
                    <span className="text-right mono font-medium">
                      {deal.dealScore != null ? `${deal.dealScore.toFixed(0)} / 100` : "—"}
                    </span>
                    <span className="text-ink-tertiary">{t("web.listing.confidence")}</span>
                    <span className="text-right text-ink-secondary capitalize">{deal.fairPriceConfidence ?? "—"}</span>
                  </div>
                </div>
              )}

              {/* Price history */}
              <PriceHistoryCard
                history={historyQ.data ?? []}
                isLoading={historyQ.isPending}
              />
            </div>
          </aside>
        </div>
      </article>

      {/* Mobile sticky CTA bar */}
      <div className="fixed bottom-0 left-0 right-0 z-50 lg:hidden bg-surface-card/95 backdrop-blur-sm border-t border-[var(--divider-weak)] px-4 py-3 flex items-center justify-between gap-3">
        <div>
          <div className="text-[18px] font-semibold text-ink-primary mono leading-none">
            {formatEur(l.currentPriceEur != null ? Number(l.currentPriceEur) : null, { locale: numberLocale })}
          </div>
          <DealChip rating={rating} size="sm" />
        </div>
        <a
          href={l.canonicalUrl}
          target="_blank"
          rel="noopener"
          className="h-10 px-5 inline-flex items-center justify-center gap-2 rounded-md bg-[var(--accent)] text-[13px] font-medium text-[var(--ink-on-accent)] hover:bg-[var(--accent-hover)] transition-colors shadow-sm"
        >
          <ExternalLinkIcon />
          {t("web.listing.openSource")}
        </a>
      </div>
      {/* Spacer for mobile sticky bar */}
      <div className="h-16 lg:hidden" />
    </>
  );
}

/* ─── Decision Block — above-the-fold "Decide in 60 seconds" ──────── */
function DecisionBlock({
  pros,
  cons,
  risks,
  deal,
  price,
  days,
}: {
  pros: Signal[];
  cons: Signal[];
  risks: Signal[];
  deal: any;
  price: number | null;
  days: number | null;
}): React.JSX.Element {
  const { t, numberLocale } = useT();
  const fair = deal?.fairPriceEur ?? null;
  const rating = (deal?.dealRating ?? "unknown") as DealRating;

  const verdict = (() => {
    if (risks.length > 0) return { text: "À vérifier avant achat", tone: "warning" as const };
    if (rating === "super") return { text: "Excellente affaire — à saisir rapidement", tone: "positive" as const };
    if (rating === "good") return { text: "Bon rapport qualité/prix", tone: "positive" as const };
    if (rating === "fair") return { text: "Prix dans la moyenne du marché", tone: "neutral" as const };
    if (rating === "high") return { text: "Au-dessus du marché — négociable", tone: "negative" as const };
    if (rating === "over") return { text: "Trop cher par rapport au marché", tone: "negative" as const };
    return { text: "Données insuffisantes pour évaluer", tone: "neutral" as const };
  })();

  const toneClasses = {
    positive: "border-deal-super/30 bg-deal-super/5",
    negative: "border-deal-over/30 bg-deal-over/5",
    warning: "border-amber-400/30 bg-amber-50/50 dark:bg-amber-500/5",
    neutral: "border-[var(--divider-weak)] bg-[var(--surface-card)]",
  };

  return (
    <div className={`mx-5 mt-6 rounded-xl border-2 ${toneClasses[verdict.tone]} p-4 sm:p-5`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <p className="text-[15px] sm:text-[17px] font-semibold text-ink-primary leading-snug">
            {verdict.text}
          </p>
          {days != null && days <= 3 && (
            <p className="mt-1 text-[12px] text-deal-super font-medium">
              ✦ En ligne depuis {days === 0 ? "aujourd'hui" : days === 1 ? "hier" : `${days} jours`} — risque de vente rapide
            </p>
          )}
          {days != null && days >= 60 && (
            <p className="mt-1 text-[12px] text-ink-tertiary">
              En ligne depuis {days} jours — marge de négociation probable
            </p>
          )}
        </div>
        {fair != null && price != null && (
          <div className="shrink-0 text-right">
            <span className="block text-[11px] uppercase tracking-wide text-ink-tertiary">vs marché</span>
            <span className={`block text-[18px] font-bold mono ${price <= fair ? "text-deal-super" : "text-deal-over"}`}>
              {price <= fair ? "−" : "+"}{formatEur(Math.abs(price - fair), { locale: numberLocale })}
            </span>
          </div>
        )}
      </div>

      {/* Pros and cons in compact pills */}
      {(pros.length > 0 || cons.length > 0) && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {pros.slice(0, 3).map((s) => (
            <span
              key={s.id}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-deal-super/10 text-deal-super text-[11px] font-medium"
            >
              + {s.label}
            </span>
          ))}
          {cons.slice(0, 3).map((s) => (
            <span
              key={s.id}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-deal-over/10 text-deal-over text-[11px] font-medium"
            >
              − {s.label}
            </span>
          ))}
          {risks.slice(0, 2).map((s) => (
            <span
              key={s.id}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-amber-100 text-amber-800 dark:bg-amber-500/10 dark:text-amber-300 text-[11px] font-medium"
            >
              ⚠ {s.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Pros / cons / risks summary ────────────────────────────────────── */
function SignalsPanel({
  pros,
  cons,
  risks,
}: {
  pros: Signal[];
  cons: Signal[];
  risks: Signal[];
}): React.JSX.Element {
  const { t } = useT();
  return (
    <section className="bg-surface-card border border-[var(--divider-weak)] rounded-lg p-5">
      <SectionLabel>{t("web.signals.title")}</SectionLabel>
      {risks.length > 0 && (
        <div className="mt-3 rounded-md border border-amber-300/40 bg-amber-50/40 dark:bg-amber-500/5 px-3.5 py-2.5 space-y-1.5">
          {risks.map((s) => (
            <div key={s.id} className="flex items-start gap-2 text-[13px] text-amber-900 dark:text-amber-200">
              <SignalIcon kind="risk" />
              <div>
                <span className="font-medium">{s.label}</span>
                {s.detail && <span className="ml-1 text-amber-800/80 dark:text-amber-200/70">— {s.detail}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
        {pros.length > 0 && (
          <div>
            <div className="text-[11px] uppercase tracking-[0.08em] text-deal-super font-medium mb-2">
              {t("web.signals.pros")}
            </div>
            <ul className="space-y-1.5">
              {pros.map((s) => (
                <li key={s.id} className="flex items-start gap-2 text-[13px] text-ink-secondary">
                  <SignalIcon kind="pro" />
                  <span>
                    <span className="text-ink-primary font-medium">{s.label}</span>
                    {s.detail && <span className="ml-1 text-ink-tertiary">· {s.detail}</span>}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {cons.length > 0 && (
          <div>
            <div className="text-[11px] uppercase tracking-[0.08em] text-deal-over font-medium mb-2">
              {t("web.signals.cons")}
            </div>
            <ul className="space-y-1.5">
              {cons.map((s) => (
                <li key={s.id} className="flex items-start gap-2 text-[13px] text-ink-secondary">
                  <SignalIcon kind="con" />
                  <span>
                    <span className="text-ink-primary font-medium">{s.label}</span>
                    {s.detail && <span className="ml-1 text-ink-tertiary">· {s.detail}</span>}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}

function SignalIcon({ kind }: { kind: "pro" | "con" | "risk" }): React.JSX.Element {
  if (kind === "pro") {
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-deal-super mt-[3px] shrink-0">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    );
  }
  if (kind === "con") {
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-deal-over mt-[3px] shrink-0">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    );
  }
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" className="text-amber-600 dark:text-amber-400 mt-[3px] shrink-0">
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    </svg>
  );
}

/* ─── Deal Analysis card ─────────────────────────────────────────────── */
type DealForAnalysis = {
  fairPriceEur: number | null;
  dealSavingsEur: number | null;
  savingsSuspect?: boolean;
};

function DealAnalysis({ deal, price }: { deal: DealForAnalysis; price: number | null }): React.JSX.Element {
  const { t, numberLocale } = useT();
  if (!price || !deal.fairPriceEur) return <></>;

  const diff = deal.fairPriceEur - price;
  const pct = Math.round((diff / deal.fairPriceEur) * 100);
  const savingsToShow = deal.dealSavingsEur ?? (diff > 0 ? diff : 0);
  const isPositive = diff > 0 && !deal.savingsSuspect && savingsToShow > 0;
  const isNegative = diff < 0;

  // Visual tone follows the ACTUAL position vs market.
  const toneCard = isPositive
    ? "bg-deal-super-soft/50 border-deal-super/20"
    : isNegative
      ? "bg-deal-over-soft/40 border-deal-over/20"
      : "bg-surface-sunken border-[var(--divider-weak)]";
  const toneIcon = isPositive ? "text-deal-super" : isNegative ? "text-deal-over" : "text-ink-tertiary";
  const toneIconBg = isPositive ? "bg-deal-super/10" : isNegative ? "bg-deal-over/10" : "bg-surface-card";

  return (
    <div className={`border rounded-lg p-5 ${toneCard}`}>
      <div className="flex items-center gap-3">
        <div className={`flex items-center justify-center w-10 h-10 rounded-full ${toneIconBg}`}>
          {isPositive ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={toneIcon}>
              <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
              <polyline points="17 6 23 6 23 12" />
            </svg>
          ) : isNegative ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={toneIcon}>
              <polyline points="23 18 13.5 8.5 8.5 13.5 1 6" />
              <polyline points="17 18 23 18 23 12" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={toneIcon}>
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          )}
        </div>
        <div>
          <div className="text-[15px] font-medium text-ink-primary">
            {isPositive
              ? t("web.signal.percentBelow", { pct: Math.abs(pct) })
              : isNegative
                ? t("web.signal.percentAbove", { pct: Math.abs(pct) })
                : "Prix dans la moyenne du marché"}
          </div>
          {deal.savingsSuspect ? (
            <div className="text-[12.5px] text-amber-700 dark:text-amber-400 mt-0.5">
              {t("web.signal.savingsSuspectDetail")}
            </div>
          ) : (
            <div className="text-[12.5px] text-ink-tertiary mt-0.5">
              {isPositive ? "Économie estimée:" : isNegative ? "Surcoût estimé:" : "Écart:"}{" "}
              <span className={`font-medium ${isPositive ? "text-deal-super" : isNegative ? "text-deal-over" : "text-ink-secondary"}`}>
                {formatEur(Math.abs(savingsToShow || diff), { locale: numberLocale })}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Percentile gauge ───────────────────────────────────────────────── */
function PercentileGauge({ price, p10, p90, median }: { price: number; p10: number; p90: number; median: number }): React.JSX.Element {
  const range = p90 - p10;
  if (range <= 0) return <></>;
  const pos = Math.max(0, Math.min(1, (price - p10) / range));
  const medPos = Math.max(0, Math.min(1, (median - p10) / range));

  return (
    <div className="mt-4">
      <div className="flex justify-between text-[10px] mono text-ink-tertiary mb-1.5">
        <span>P10</span>
        <span>Médiane</span>
        <span>P90</span>
      </div>
      <div className="relative h-2.5 bg-surface-sunken rounded-full overflow-hidden">
        {/* Gradient from green (cheap) to red (expensive) */}
        <div className="absolute inset-0 bg-gradient-to-r from-deal-super/60 via-deal-fair/60 to-deal-over/60 rounded-full" />
        {/* Median marker */}
        <div
          className="absolute top-0 bottom-0 w-px bg-ink-tertiary/50"
          style={{ left: `${medPos * 100}%` }}
        />
        {/* Price indicator */}
        <div
          className="absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full bg-ink-primary border-2 border-surface-card shadow-sm transition-all duration-500"
          style={{ left: `calc(${pos * 100}% - 7px)` }}
        />
      </div>
    </div>
  );
}

/* ─── Specs grid ─────────────────────────────────────────────────────── */
type SpecsListing = {
  currentYear: number | null;
  currentKm: number | null;
  currentDoors: number | null;
  currentSeats: number | null;
  currentEngineCc: number | null;
  currentDriveType: string | null;
  currentCo2: number | null;
  currentFirstRegistration: string | null;
  // From the latest snapshot — joined in `listings.byId`.
  snapFuel?: string | null;
  snapGearbox?: string | null;
  snapHp?: number | null;
  snapBody?: string | null;
  snapColorExt?: string | null;
  snapColorInt?: string | null;
  snapLocationText?: string | null;
  snapSellerName?: string | null;
  snapSellerType?: string | null;
  daysOnline?: number | null;
};

function SpecsGrid({ listing }: { listing: SpecsListing }): React.JSX.Element | null {
  const { t, numberLocale } = useT();

  const specs: Array<{ label: string; value: string }> = [];

  if (listing.currentYear) specs.push({ label: t("web.list.col.year"), value: String(listing.currentYear) });
  if (listing.currentKm != null)
    specs.push({ label: t("web.list.col.km"), value: `${new Intl.NumberFormat(numberLocale).format(listing.currentKm)} km` });
  if (listing.snapFuel) specs.push({ label: t("web.listing.specs.fuel"), value: listing.snapFuel });
  if (listing.snapGearbox) specs.push({ label: t("web.listing.specs.gearbox"), value: listing.snapGearbox });
  if (listing.snapHp) specs.push({ label: t("web.listing.specs.hp"), value: `${listing.snapHp} ch` });
  if (listing.snapBody) specs.push({ label: t("web.listing.specs.body"), value: listing.snapBody });
  if (listing.snapColorExt) specs.push({ label: t("web.listing.specs.color"), value: listing.snapColorExt });
  if (listing.currentDoors) specs.push({ label: t("web.listing.specs.doors"), value: String(listing.currentDoors) });
  if (listing.currentSeats) specs.push({ label: t("web.listing.specs.seats"), value: String(listing.currentSeats) });
  if (listing.currentEngineCc)
    specs.push({
      label: t("web.listing.specs.engineCc"),
      value: `${new Intl.NumberFormat(numberLocale).format(listing.currentEngineCc)} cm³`,
    });
  if (listing.currentDriveType)
    specs.push({
      label: t("web.listing.specs.driveType"),
      value: DRIVE_TYPE_LABELS[listing.currentDriveType] ?? listing.currentDriveType,
    });
  if (listing.currentCo2) specs.push({ label: t("web.listing.specs.co2"), value: `${listing.currentCo2} g/km` });
  if (listing.currentFirstRegistration)
    specs.push({ label: t("web.listing.specs.firstRegistration"), value: listing.currentFirstRegistration });
  if (listing.snapLocationText)
    specs.push({ label: t("web.listing.location"), value: listing.snapLocationText });
  if (listing.snapSellerType)
    specs.push({
      label: t("web.listing.seller"),
      value:
        listing.snapSellerType === "pro"
          ? t("web.signal.sellerPro")
          : listing.snapSellerType === "private"
            ? t("web.signal.sellerPrivate")
            : listing.snapSellerType,
    });
  if (listing.daysOnline != null)
    specs.push({
      label: t("web.listing.daysOnline"),
      value: t("web.listing.daysValue", { days: listing.daysOnline }),
    });

  if (specs.length === 0) return null;

  return (
    <section>
      <SectionLabel>{t("web.listing.specs")}</SectionLabel>
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-4">
        {specs.map((s) => (
          <div key={s.label} className="flex flex-col">
            <span className="text-[11px] text-ink-tertiary uppercase tracking-[0.04em]">{s.label}</span>
            <span className="text-[14px] text-ink-primary mt-0.5 font-medium">{s.value}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ─── Gallery ────────────────────────────────────────────────────────── */
function Gallery({
  images,
  pendingLabel,
}: {
  images: Array<{ url: string; key: string }>;
  pendingLabel: string;
}): React.JSX.Element {
  const [lightboxIdx, setLightboxIdx] = React.useState<number | null>(null);

  if (images.length === 0) {
    return (
      <div className="mx-5 h-[260px] sm:h-[400px] rounded-lg border border-[var(--divider-weak)] bg-surface-sunken flex flex-col items-center justify-center gap-3 text-ink-tertiary">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" className="opacity-30">
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <path d="M21 15l-5-5L5 21" />
        </svg>
        <span className="text-[13px]">{pendingLabel}</span>
      </div>
    );
  }

  const [hero, ...rest] = images;
  const thumbnailCount = Math.min(rest.length, 5);

  return (
    <>
      <div className="px-5">
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_200px] lg:grid-cols-[1fr_280px] gap-2">
          {/* Hero image */}
          {hero && (
            <button
              onClick={() => setLightboxIdx(0)}
              className="relative overflow-hidden rounded-lg border border-[var(--divider-weak)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] group sm:row-span-2"
            >
              <img
                src={hero.url}
                alt=""
                className="w-full h-[280px] sm:h-[420px] lg:h-[500px] object-cover transition-transform duration-500 group-hover:scale-[1.015]"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors duration-300" />
              <span className="absolute bottom-3 right-3 mono text-[11px] bg-black/70 text-white px-2.5 py-1 rounded-md backdrop-blur-sm">
                {images.length} photos
              </span>
            </button>
          )}
          {/* Side thumbnails (desktop only) */}
          <div className="hidden sm:grid grid-rows-2 gap-2">
            {rest.slice(0, 2).map((im, i) => (
              <button
                key={im.key}
                onClick={() => setLightboxIdx(i + 1)}
                className="relative overflow-hidden rounded-lg border border-[var(--divider-weak)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] group"
              >
                <img
                  src={im.url}
                  alt=""
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  loading="lazy"
                />
                {i === 1 && rest.length > 2 && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50 text-white text-[14px] font-medium backdrop-blur-[2px]">
                    +{rest.length - 2} photos
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Thumbnail strip (mobile + below on desktop) */}
        <div className="mt-2 flex gap-2 overflow-x-auto pb-2 sm:hidden scrollbar-hide">
          {rest.slice(0, 8).map((im, i) => (
            <button
              key={im.key}
              onClick={() => setLightboxIdx(i + 1)}
              className="relative flex-shrink-0 w-[100px] h-[70px] overflow-hidden rounded-md border border-[var(--divider-weak)] focus:outline-none group"
            >
              <img
                src={im.url}
                alt=""
                className="w-full h-full object-cover"
                loading="lazy"
              />
              {i === 7 && rest.length > 8 && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50 text-white text-[12px] font-medium">
                  +{rest.length - 8}
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {lightboxIdx !== null && (
        <Lightbox
          images={images}
          startIndex={lightboxIdx}
          onClose={() => setLightboxIdx(null)}
        />
      )}
    </>
  );
}

/* ─── Lightbox ───────────────────────────────────────────────────────── */
function Lightbox({
  images,
  startIndex,
  onClose,
}: {
  images: Array<{ url: string; key: string }>;
  startIndex: number;
  onClose: () => void;
}): React.JSX.Element {
  const [idx, setIdx] = React.useState(startIndex);

  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setIdx((i) => Math.min(i + 1, images.length - 1));
      if (e.key === "ArrowLeft") setIdx((i) => Math.max(i - 1, 0));
    };
    window.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [images.length, onClose]);

  return (
    <div
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-black/95 backdrop-blur-md"
      onClick={onClose}
    >
      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 py-3 z-10">
        <span className="mono text-[13px] text-white/80">
          {idx + 1} / {images.length}
        </span>
        <button
          onClick={onClose}
          className="text-white/80 hover:text-white w-9 h-9 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      {/* Main image */}
      <div className="relative max-w-[92vw] max-h-[80vh] flex items-center" onClick={(e) => e.stopPropagation()}>
        <img
          src={images[idx]!.url}
          alt=""
          className="max-w-full max-h-[80vh] object-contain rounded-md"
        />
      </div>

      {/* Navigation arrows */}
      {idx > 0 && (
        <button
          onClick={(e) => { e.stopPropagation(); setIdx(idx - 1); }}
          className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors backdrop-blur-sm"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
      )}
      {idx < images.length - 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); setIdx(idx + 1); }}
          className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors backdrop-blur-sm"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      )}

      {/* Thumbnail strip in lightbox */}
      <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-1.5 px-4 overflow-x-auto scrollbar-hide">
        {images.slice(0, 20).map((im, i) => (
          <button
            key={im.key}
            onClick={(e) => { e.stopPropagation(); setIdx(i); }}
            className={`flex-shrink-0 w-[52px] h-[36px] rounded-[4px] overflow-hidden border-2 transition-all duration-200 ${
              i === idx ? "border-white scale-110" : "border-transparent opacity-60 hover:opacity-100"
            }`}
          >
            <img src={im.url} alt="" className="w-full h-full object-cover" loading="lazy" />
          </button>
        ))}
      </div>
    </div>
  );
}

/* ─── Price history (mini chart) ─────────────────────────────────────── */
type HistoryRow = {
  id: string;
  observed_at: Date | string;
  change_kind: string;
  price_eur: string | null;
};

function PriceHistoryCard({
  history,
  isLoading,
}: {
  history: HistoryRow[];
  isLoading: boolean;
}): React.JSX.Element | null {
  const { t, numberLocale } = useT();
  if (isLoading) return null;

  // Keep only rows with a price; sort ascending by date.
  const points = history
    .filter((h) => h.price_eur != null)
    .map((h) => ({
      t: typeof h.observed_at === "string" ? new Date(h.observed_at).getTime() : h.observed_at.getTime(),
      price: Number(h.price_eur),
    }))
    .sort((a, b) => a.t - b.t);

  if (points.length === 0) {
    return (
      <div className="bg-surface-card border border-[var(--divider-weak)] rounded-lg p-5">
        <SectionLabel>{t("web.listing.priceHistory")}</SectionLabel>
        <p className="mt-3 text-[12.5px] text-ink-tertiary">{t("web.listing.priceHistoryEmpty")}</p>
      </div>
    );
  }

  // De-duplicate consecutive identical prices to keep the chart readable.
  const dedup: typeof points = [];
  for (const p of points) {
    if (dedup.length === 0 || dedup[dedup.length - 1]!.price !== p.price) dedup.push(p);
    else dedup[dedup.length - 1] = p;
  }

  const minP = Math.min(...dedup.map((p) => p.price));
  const maxP = Math.max(...dedup.map((p) => p.price));
  const minT = dedup[0]!.t;
  const maxT = dedup[dedup.length - 1]!.t;
  const W = 280;
  const H = 70;
  const range = Math.max(1, maxP - minP);
  const span = Math.max(1, maxT - minT);
  const path = dedup
    .map((p, i) => {
      const x = ((p.t - minT) / span) * W;
      const y = H - ((p.price - minP) / range) * H;
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ");
  const last = dedup[dedup.length - 1]!;
  const first = dedup[0]!;
  const delta = last.price - first.price;
  const deltaPct = first.price > 0 ? (delta / first.price) * 100 : 0;

  return (
    <div className="bg-surface-card border border-[var(--divider-weak)] rounded-lg p-5">
      <SectionLabel>{t("web.listing.priceHistory")}</SectionLabel>
      <div className="mt-3 flex items-baseline justify-between">
        <span className="text-[20px] font-semibold mono tabular-nums text-ink-primary">
          {formatEur(last.price, { locale: numberLocale })}
        </span>
        {dedup.length > 1 && (
          <span
            className={
              delta < 0
                ? "text-[12px] mono text-deal-super"
                : delta > 0
                  ? "text-[12px] mono text-deal-over"
                  : "text-[12px] mono text-ink-tertiary"
            }
          >
            {delta < 0 ? "▼" : delta > 0 ? "▲" : "·"} {formatEur(Math.abs(delta), { locale: numberLocale })} (
            {deltaPct >= 0 ? "+" : ""}
            {deltaPct.toFixed(1)}%)
          </span>
        )}
      </div>
      {dedup.length > 1 && (
        <svg viewBox={`0 0 ${W} ${H}`} className="mt-3 w-full h-[70px]" preserveAspectRatio="none">
          <path
            d={`${path} L${W} ${H} L0 ${H} Z`}
            fill={delta < 0 ? "var(--deal-super)" : "var(--deal-over)"}
            opacity="0.08"
          />
          <path
            d={path}
            fill="none"
            stroke={delta < 0 ? "var(--deal-super)" : delta > 0 ? "var(--deal-over)" : "var(--ink-tertiary)"}
            strokeWidth="1.5"
            vectorEffect="non-scaling-stroke"
          />
          {dedup.map((p, i) => {
            const x = ((p.t - minT) / span) * W;
            const y = H - ((p.price - minP) / range) * H;
            return <circle key={i} cx={x} cy={y} r="2" fill="var(--ink-primary)" />;
          })}
        </svg>
      )}
      <div className="mt-2 flex justify-between text-[10px] mono text-ink-tertiary">
        <span>
          {new Date(first.t).toLocaleDateString(numberLocale, { day: "2-digit", month: "short" })}
        </span>
        <span>
          {new Date(last.t).toLocaleDateString(numberLocale, { day: "2-digit", month: "short" })}
        </span>
      </div>
    </div>
  );
}

/* ─── Utility components ─────────────────────────────────────────────── */
function SectionLabel({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <h2 className="mono text-[10.5px] uppercase tracking-[0.12em] text-ink-tertiary font-medium">
      {children}
    </h2>
  );
}

function SourceBadge({ source }: { source: string }): React.JSX.Element {
  const label = source
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-medium text-ink-secondary bg-surface-sunken px-2 py-0.5 rounded-[3px]">
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="opacity-60">
        <circle cx="12" cy="12" r="10" />
        <path d="M2 12h20" />
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
      </svg>
      {label}
    </span>
  );
}

function ExternalLinkIcon(): React.JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

/* ─── Options & Equipment Section ────────────────────────────────────── */

const CATEGORY_ICONS: Record<string, React.JSX.Element> = {
  performance: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>,
  exterior: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="3" width="22" height="18" rx="3"/></svg>,
  interior: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>,
  technology: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>,
  safety: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  comfort: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>,
  audio: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>,
  wheels: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="3"/></svg>,
  lighting: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="3"/><circle cx="12" cy="12" r="5"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="1" y1="12" x2="3" y2="12"/></svg>,
  paint: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/></svg>,
  package: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>,
};

const CATEGORY_LABELS: Record<string, string> = {
  performance: "Performance",
  exterior: "Extérieur",
  interior: "Intérieur",
  technology: "Technologie",
  safety: "Sécurité",
  comfort: "Confort",
  audio: "Audio",
  wheels: "Roues",
  lighting: "Éclairage",
  paint: "Peinture",
  package: "Packs",
};

interface OptionRow {
  id: string;
  name: string;
  category: string | null;
  oemCode: string | null;
  confidence: string | null;
  source: string;
}

function OptionsSection({
  options,
  isLoading,
  listingId,
  llmEnrichedAt,
  onEnrich,
  enriching,
}: {
  options: OptionRow[];
  isLoading: boolean;
  listingId: string;
  llmEnrichedAt: string | null;
  onEnrich: () => void;
  enriching: boolean;
}): React.JSX.Element {
  if (isLoading) return <></>;

  // Group by category
  const grouped = options.reduce<Record<string, OptionRow[]>>((acc, opt) => {
    const cat = opt.category || "other";
    if (!acc[cat]) acc[cat] = [];
    acc[cat]!.push(opt);
    return acc;
  }, {});

  const categories = Object.keys(grouped).sort((a, b) => {
    const order = ["package", "performance", "exterior", "interior", "technology", "safety", "comfort", "audio", "wheels", "lighting", "paint"];
    return order.indexOf(a) - order.indexOf(b);
  });

  if (options.length === 0 && !llmEnrichedAt) {
    return (
      <section>
        <SectionLabel>Options & Équipements</SectionLabel>
        <div className="mt-3 flex items-center gap-3">
          <button
            onClick={onEnrich}
            disabled={enriching}
            className="inline-flex items-center gap-2 h-9 px-4 rounded-md bg-[var(--accent)]/10 text-[var(--accent)] text-[13px] font-medium hover:bg-[var(--accent)]/20 transition-colors disabled:opacity-50"
          >
            {enriching ? (
              <svg className="animate-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v4m0 12v4m10-10h-4M6 12H2m15.07-5.07l-2.83 2.83M9.76 14.24l-2.83 2.83m11.14 0l-2.83-2.83M9.76 9.76L6.93 6.93"/></svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2a10 10 0 1 0 10 10"/><path d="M12 12l7-7"/><circle cx="12" cy="12" r="3"/></svg>
            )}
            Analyser les options
          </button>
          <span className="text-[11.5px] text-ink-tertiary">Extraction IA des options depuis la description</span>
        </div>
      </section>
    );
  }

  if (options.length === 0) return <></>;

  return (
    <section>
      <SectionLabel>Options & Équipements ({options.length})</SectionLabel>
      <div className="mt-4 space-y-4">
        {categories.map((cat) => (
          <div key={cat}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-ink-tertiary">{CATEGORY_ICONS[cat] ?? CATEGORY_ICONS.package}</span>
              <span className="text-[12px] font-medium text-ink-secondary uppercase tracking-[0.06em]">
                {CATEGORY_LABELS[cat] ?? cat}
              </span>
              <span className="text-[10.5px] text-ink-tertiary mono">({grouped[cat]!.length})</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {grouped[cat]!.map((opt) => (
                <span
                  key={opt.id}
                  className="inline-flex items-center gap-1.5 text-[12.5px] text-ink-secondary bg-surface-sunken px-2.5 py-1 rounded-md border border-[var(--divider-weak)]"
                  title={opt.oemCode ? `Code: ${opt.oemCode}` : undefined}
                >
                  {opt.name}
                  {opt.oemCode && (
                    <span className="mono text-[10px] text-ink-tertiary opacity-70">{opt.oemCode}</span>
                  )}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
